import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapPin, Search, Ban, Plus, Trash2, Check } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BadgePill } from "@/components/Brand";
import { toast } from "sonner";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const VENUE_CAPS = { cafe: 12, coffee: 12, library: 15, park: 30, garden: 30, plaza: 40, courthouse: 40, lawn: 20, church: 20 };
function venueCap(cat) {
  const c = (cat || "").toLowerCase();
  for (const k of Object.keys(VENUE_CAPS)) if (c.includes(k)) return VENUE_CAPS[k];
  return 20;
}

export default function CreateStudy() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [form, setForm] = useState({ title: "", description: "", book: "" });
  const [schedule, setSchedule] = useState({ frequency: "weekly", day_of_week: "2", start_time: "18:30", duration_minutes: "90" });
  const [venueQ, setVenueQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [venue, setVenue] = useState(null);
  const [sessions, setSessions] = useState([{ date: "", passage_ref: "" }]);
  const [saving, setSaving] = useState(false);

  if (user && user.tier !== "curator" && user.role !== "admin") {
    return (
      <div className="mx-auto max-w-lg px-4 py-16 text-center">
        <h1 className="font-display text-2xl font-bold text-[#0F3D66]">Curator plan required</h1>
        <p className="mt-2 text-[#6B6763]">Creating and curating your own study is a Curator feature.</p>
        <Button onClick={() => nav("/upgrade")} data-testid="need-upgrade-btn" className="mt-5 rounded-full bg-[#F36353] text-white hover:bg-[#D94A3D]">Upgrade to Curator</Button>
      </div>
    );
  }

  const search = async () => {
    if (venueQ.trim().length < 2) return;
    setSearching(true);
    try { const { data } = await api.get("/venues/search", { params: { q: venueQ } }); setResults(data.results); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSearching(false); }
  };

  const setSession = (i, field, val) => setSessions(sessions.map((s, k) => (k === i ? { ...s, [field]: val } : s)));

  const submit = async () => {
    if (!venue) return toast.error("Select a public venue first");
    if (!form.title || !form.book) return toast.error("Add a title and book");
    setSaving(true);
    try {
      const payload = {
        ...form,
        venue,
        schedule: { frequency: schedule.frequency, day_of_week: Number(schedule.day_of_week), start_time: schedule.start_time, duration_minutes: Number(schedule.duration_minutes) },
        sessions: sessions.filter((s) => s.date && s.passage_ref),
        capacity: venueCap(venue.category),
      };
      const { data } = await api.post("/studies", payload);
      toast.success("Study created");
      nav(`/study/${data.id}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="mx-auto max-w-2xl px-4 py-6 sm:px-6">
      <h1 className="font-display text-2xl font-bold tracking-tight text-[#0F3D66] sm:text-3xl">Create a study series</h1>
      <p className="mt-1 text-sm text-[#6B6763]">Studies meet only in public places. Home &amp; private addresses are blocked.</p>

      <div className="mt-6 space-y-5">
        <Field label="Study title">
          <Input data-testid="create-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Genesis Mornings at Radio Coffee" className="rounded-xl border-[#E8E4DF]" />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Book of the Bible">
            <Input data-testid="create-book" value={form.book} onChange={(e) => setForm({ ...form, book: e.target.value })} placeholder="Genesis" className="rounded-xl border-[#E8E4DF]" />
          </Field>
          <Field label="Frequency">
            <Select value={schedule.frequency} onValueChange={(v) => setSchedule({ ...schedule, frequency: v })}>
              <SelectTrigger data-testid="create-frequency" className="rounded-xl border-[#E8E4DF]"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="weekly">Weekly</SelectItem><SelectItem value="biweekly">Biweekly</SelectItem></SelectContent>
            </Select>
          </Field>
        </div>
        <Field label="Description">
          <Textarea data-testid="create-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Who it's for, what to expect…" className="rounded-xl border-[#E8E4DF]" />
        </Field>

        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Day">
            <Select value={schedule.day_of_week} onValueChange={(v) => setSchedule({ ...schedule, day_of_week: v })}>
              <SelectTrigger data-testid="create-day" className="rounded-xl border-[#E8E4DF]"><SelectValue /></SelectTrigger>
              <SelectContent>{DAYS.map((d, i) => <SelectItem key={d} value={String(i)}>{d}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Start time">
            <Input data-testid="create-time" type="time" value={schedule.start_time} onChange={(e) => setSchedule({ ...schedule, start_time: e.target.value })} className="rounded-xl border-[#E8E4DF]" />
          </Field>
          <Field label="Duration (min)">
            <Input data-testid="create-duration" type="number" value={schedule.duration_minutes} onChange={(e) => setSchedule({ ...schedule, duration_minutes: e.target.value })} className="rounded-xl border-[#E8E4DF]" />
          </Field>
        </div>

        {/* Venue */}
        <div>
          <Label className="text-[#0F3D66]">Public venue</Label>
          <div className="mt-1 flex gap-2">
            <Input data-testid="venue-search-input" value={venueQ} onChange={(e) => setVenueQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), search())} placeholder="Search a café, library, park…" className="rounded-xl border-[#E8E4DF]" />
            <Button onClick={search} disabled={searching} data-testid="venue-search-btn" className="rounded-xl bg-[#748C70] text-white hover:bg-[#5f7259]"><Search size={16} /></Button>
          </div>
          {venue && (
            <div className="mt-2 flex flex-wrap items-center gap-2 rounded-xl bg-[#748C70]/10 p-3 text-sm" data-testid="selected-venue">
              <Check size={16} className="text-[#4f6a4b]" />
              <span className="font-medium text-[#0F3D66]">{venue.name}</span>
              <BadgePill tone="sage">{venue.category}</BadgePill>
              <span className="w-full text-xs text-[#6B6763]" data-testid="capacity-helper">Max group size for a {venue.category.toLowerCase()} venue is {venueCap(venue.category)} (3-person minimum).</span>
            </div>
          )}
          <div className="mt-2 space-y-2">
            {results.map((r, i) => (
              <button key={i} disabled={!r.allowed} data-testid={`venue-result-${i}`}
                onClick={() => { setVenue({ name: r.name, category: r.category, address: r.address, lat: r.lat, lng: r.lng }); setResults([]); }}
                className={`flex w-full items-start gap-2 rounded-xl border p-3 text-left ${r.allowed ? "border-[#E8E4DF] bg-white hover:border-[#F36353]" : "cursor-not-allowed border-[#F0EBE4] bg-[#FAF6F0] opacity-70"}`}>
                {r.allowed ? <MapPin size={16} className="mt-0.5 shrink-0 text-[#F36353]" /> : <Ban size={16} className="mt-0.5 shrink-0 text-red-400" />}
                <div className="min-w-0">
                  <div className="text-sm font-medium text-[#0F3D66]">{r.name} <span className="text-xs font-normal text-[#6B6763]">· {r.category}</span></div>
                  <div className="truncate text-xs text-[#6B6763]">{r.address}</div>
                  {!r.allowed && <div className="text-xs font-medium text-red-500">{r.reason}</div>}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Sessions */}
        <div>
          <Label className="text-[#0F3D66]">Passages per session</Label>
          <div className="mt-2 space-y-2">
            {sessions.map((s, i) => (
              <div key={i} className="flex gap-2">
                <Input data-testid={`session-date-${i}`} type="date" value={s.date} onChange={(e) => setSession(i, "date", e.target.value)} className="rounded-xl border-[#E8E4DF]" />
                <Input data-testid={`session-ref-${i}`} value={s.passage_ref} onChange={(e) => setSession(i, "passage_ref", e.target.value)} placeholder="Genesis 1:1-3" className="rounded-xl border-[#E8E4DF]" />
                {sessions.length > 1 && <Button variant="outline" size="icon" onClick={() => setSessions(sessions.filter((_, k) => k !== i))} className="shrink-0 rounded-xl border-[#E8E4DF]"><Trash2 size={15} /></Button>}
              </div>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={() => setSessions([...sessions, { date: "", passage_ref: "" }])} data-testid="add-session-btn" className="mt-2 rounded-full border-[#E8E4DF]"><Plus size={14} className="mr-1" /> Add session</Button>
        </div>

        <Button onClick={submit} disabled={saving} data-testid="create-study-submit" className="h-12 w-full rounded-full bg-[#F36353] text-base text-white hover:bg-[#D94A3D]">
          {saving ? "Creating…" : "Create study"}
        </Button>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div><Label className="text-[#0F3D66]">{label}</Label><div className="mt-1">{children}</div></div>
);
